import React, { useMemo } from 'react'
import { ControllingRow, ControllingAggregates } from '@myapp/contract'
import { DataTable, Pagination, TableColumn } from '../../../../shared/ui'
import {
  CONTROLLING_COLUMNS,
  fmtHours,
  fmtDeviation,
  deviationClass,
} from '../../../controlling/ui/model/columns'
import { routes } from 'src/shared/config/routes'
import { useNavigate } from 'react-router-dom'

const GESAMT_COLUMN: TableColumn<ControllingRow> = {
  key: 'totalHours',
  header: 'Gesamt-Std.',
  headerClassName: 'min-w-[120px]',
  cellClassName: 'whitespace-nowrap font-medium',
  render: (r) => fmtHours(r.plannedTotalHours, r.actualTotalHours),
}

const travelIdx = CONTROLLING_COLUMNS.findIndex((c) => c.key === 'travelHours')
const AUFTRAEGE_COLUMNS: TableColumn<ControllingRow>[] = [
  ...CONTROLLING_COLUMNS.slice(0, travelIdx + 1),
  GESAMT_COLUMN,
  ...CONTROLLING_COLUMNS.slice(travelIdx + 1),
]

interface AuftraegeListProps {
  items: ControllingRow[]
  aggregates: ControllingAggregates | undefined
  meta: { page: number; limit: number; total: number } | undefined
  isLoading?: boolean
  isFetching?: boolean
  error?: unknown
  page: number
  onPageChange: (p: number) => void
}

export const AuftraegeList: React.FC<AuftraegeListProps> = ({
  items,
  aggregates,
  meta,
  isLoading,
  isFetching,
  error,
  page,
  onPageChange,
}) => {
  const navigate = useNavigate()
  const totalPages = meta ? Math.ceil(meta.total / meta.limit) : 0

  const totals = useMemo(() => {
    if (!aggregates) return null
    return {
      plannedWork: aggregates.totalPlannedWorkHours,
      actualWork: aggregates.totalActualWorkHours,
      plannedTravel: aggregates.totalPlannedTravelHours,
      actualTravel: aggregates.totalActualTravelHours,
      plannedTotal: aggregates.totalPlannedHours,
      actualTotal: aggregates.totalActualHours,
      avgDeviation: aggregates.deviation,
    }
  }, [aggregates])

  const C = 'px-4 py-2.5'
  const totalsRow = totals ? (
    <tr className="bg-gray-100 border-t-2 border-gray-200 text-gray-600 text-xs">
      <td className={`${C} align-top leading-tight`}>
        <span className="font-medium text-gray-700">Gesamt</span>
        <br />
        <span className="text-gray-500">{meta?.total ?? 0} Aufträge</span>
      </td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top text-gray-400`}>–</td>
      <td className={`${C} align-top whitespace-nowrap font-medium`}>
        {fmtHours(totals.plannedWork, totals.actualWork)}
      </td>
      <td className={`${C} align-top whitespace-nowrap font-medium`}>
        {fmtHours(totals.plannedTravel, totals.actualTravel)}
      </td>
      <td className={`${C} align-top whitespace-nowrap font-medium`}>
        {fmtHours(totals.plannedTotal, totals.actualTotal)}
      </td>
      <td className={`${C} align-top whitespace-nowrap font-medium ${deviationClass(totals.avgDeviation)}`}>
        {fmtDeviation(totals.avgDeviation)}
      </td>
    </tr>
  ) : undefined

  return (
    <div className="flex flex-col">
      <DataTable<ControllingRow>
        columns={AUFTRAEGE_COLUMNS}
        rows={items}
        getRowKey={(r, i) => `${r.workOrderId}-${i}`}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        emptyMessage="Keine Ergebnisse"
        footerRow={totalsRow}
        compact
        minWidth={1280}
        onRowClick={(r) => navigate(routes.task.detail(r.workOrderId))}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        isFetching={isFetching}
        disabled={isLoading}
      />
    </div>
  )
}
